import {
  Box,
  Button,
  Flex,
  Stack,
  FormErrorMessage,
  FormControl,
} from "@chakra-ui/react";
import { FC, useState, useEffect, useRef } from "react";
import { FieldValues, useFieldArray, UseFormWatch } from "react-hook-form";
import { Toast } from "../../../components/atom/Toast/Toast";
import InputFieldComponent from "../../../components/molecule/InputField/InputFieldComponent";
import ModalComponent from "../../../components/organism/Modal/ModalComponent";
import ModalFooterComponent from "../../../components/organism/Modal/ModalFooterComponent";
import { ModalConfig } from "../../../types/Components/Modal/ModalConfig";
import { ALERT_SAN_URL } from "../../../utils/endpoints";
import { handleDeleteItem } from "../../../helpers/handleDeleteItem";
import { AuthUser } from "../../../types/User/AuthUser";
import TextAreaComponent from "../../../components/molecule/TextArea/TextAreaComponent";
import api from "../../../utils/api";
import { handleSubmitItem } from "../../../helpers/handleSubmitItem";
import IndicatorAlertSanField from "./IndicatorAlertSanField";
import FormSelect from "../../../components/molecule/Select/FormSelect";
import { getCollectionFrequency } from "../../../services/Indicator/getCollectionFrequency";

interface ModalAlertSanProps {
  idItem?: number;
  errors: any;
  handleSubmit: any;
  control: any;
  modalConfig: ModalConfig;
  watch: UseFormWatch<FieldValues>;
  user: AuthUser;
}

const ModalAlertSan: FC<ModalAlertSanProps> = ({
  idItem,
  errors,
  handleSubmit,
  control,
  modalConfig,
  watch,
  user,
}) => {
  const { isOpen, title = "", buttonTitle, closeModal } = modalConfig;
  const [isLoaded, setIsLoaded] = useState(true);
  const { addToast } = Toast();
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const frequencyOption = watch("frequencyOption");
  const prevFrequencyId = useRef<number | undefined>(frequencyOption?.id);

  const onSubmit = async (data: FieldValues) => {
    setIsLoaded(false);
    const indicatorsData = data.indicators || [];
    const indicators = indicatorsData
      .filter((indicator: any) => indicator.indicatorOptions?.id)
      .map((indicator: any) => indicator.indicatorOptions.id);

    const bodyRequest = {
      name: data.name,
      rationalDescription: data.rationalDescription,
      impactDescription: data.impactDescription,
      frequencyId: frequencyOption?.id,
      indicatorIds: indicators,
      ...(idItem && idItem !== 0 ? { id: idItem } : {}),
    };

    const success = await handleSubmitItem(
      () =>
        idItem && idItem !== 0
          ? api.put(`${ALERT_SAN_URL}/${idItem}`, bodyRequest)
          : api.post(ALERT_SAN_URL, bodyRequest),
      addToast,
      setIsLoaded,
      idItem === 0 ? "Create" : "Update",
      idItem === 0 ? "Alerta SAN criado" : "Alterações Guardadas",
    );

    console.log(success);
    if (success) {
      setIsLoaded(true);
      closeModal();
    }
  };

  const handleDelete = async () => {
    if (idItem) {
      await handleDeleteItem(
        ALERT_SAN_URL,
        setIsAlertOpen,
        idItem,
        addToast,
        setIsLoaded,
      );
      closeModal();
    }
  };

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "indicators",
  });
  const isInitialLoad = useRef(true);

  useEffect(() => {
    if (!isOpen) {
      prevFrequencyId.current = undefined;
      isInitialLoad.current = true;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const currentFrequencyId = frequencyOption?.id;

    if (isInitialLoad.current) {
      prevFrequencyId.current = currentFrequencyId;
      isInitialLoad.current = false;
      return;
    }

    if (prevFrequencyId.current !== currentFrequencyId) {
      replace([]);
      prevFrequencyId.current = currentFrequencyId;
    }
  }, [frequencyOption?.id, isOpen, replace]);

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={closeModal}
      title={title}
      isAlertOpen={isAlertOpen}
      setIsAlertOpen={setIsAlertOpen}
      handleDelete={handleDelete}
      alertText="Tem a certeza que pretende eliminar este alerta san?"
      alertTitle="Eliminar Alerta SAN"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={4} pb="2">
          <InputFieldComponent
            label="Nome"
            name="name"
            type="text"
            register={control.register}
            errors={errors}
            isRequired
          />
          <TextAreaComponent
            label="Descreva o racional científico deste Alerta SAN"
            name="rationalDescription"
            register={control.register}
            errors={errors}
            isRequired
          />
          <TextAreaComponent
            label="Insira o impacto esperado do Alerta SAN"
            name="impactDescription"
            register={control.register}
            errors={errors}
            isRequired
          />
          <FormSelect
            control={control}
            errors={errors}
            watch={watch}
            fieldName="frequencyOption"
            fieldLabel="Frequência"
            fetchOptions={getCollectionFrequency}
          />
          <Box>
            <Box
              border={errors.indicators ? "3px solid" : "none"}
              borderColor={errors.indicators ? "red.500" : "transparent"}
              borderRadius="md"
              p={errors.indicators ? 4 : 0}
            >
              <Flex gap="8" direction="column">
                <Box>
                  <Button
                    variant="primary"
                    onClick={() => append({ indicatorOptions: "" })}
                    isDisabled={!frequencyOption?.id}
                  >
                    Adicionar indicador ao Alerta
                  </Button>
                </Box>
                {fields.map((field, index) => (
                  <IndicatorAlertSanField
                    key={field.id}
                    control={control}
                    errors={errors}
                    watch={watch}
                    index={index}
                    remove={() => remove(index)}
                    frequencyId={watch("frequencyOption")?.id}
                  />
                ))}
              </Flex>
            </Box>
            {errors.indicators && (
              <FormControl isInvalid={!!errors.indicators}>
                <FormErrorMessage>
                  {errors.indicators.message ||
                    errors.indicators.root?.message ||
                    errors.indicators._errors?.[0] ||
                    "Selecione pelo menos um indicador"}
                </FormErrorMessage>
              </FormControl>
            )}
          </Box>
        </Stack>
        <ModalFooterComponent
          setIsAlertOpen={setIsAlertOpen}
          setIsOpen={closeModal}
          buttonTitle={buttonTitle}
          deleteButtonName="Eliminar Alerta SAN"
          deleteEnabled={
            user?.permissions
              ? user.permissions.includes("DeleteAlertSan")
              : false
          }
          isLoaded={isLoaded}
        />
      </form>
    </ModalComponent>
  );
};

export default ModalAlertSan;
